import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { FanFreeformSlideCard } from '../fan/fanSlideCanvas';
import fanStyles from '../fan/mobile-view.module.css';
import { FanReactionButtons } from '@/components/FanReactionButtons';
import {
  fitSlideFrame,
  isFreeformLayout,
  shouldShowFreeformStageJoinCta,
  SLIDE_QR_CAPTION_RESERVE_FONT_SIZE,
  SLIDE_QR_LABEL_FONT_SIZE,
  SLIDE_QR_ZONE,
  slideFrameJoinOverlayStyle,
  slidePercentBoxStyle,
  slideQrPillMaxBox,
} from '../slideFrame';
import {
  getFreeformStageQrFootprintPx,
  qrCodePropsFromFootprint,
  STAGE_JOIN_QR_BG_COLOR,
  STAGE_JOIN_QR_FG_COLOR,
  stageQrCaptionClassName,
} from '@/routes/stage/stageJoinQr';
import {
  StageSlideCanvas,
  type StageSlideCanvasStyles,
} from '../stage/stageSlideCanvas';
import stageStyles from '../stage/stage-view.module.css';
import {
  previewSlideData,
  STAGE_THEME_BACKGROUND,
  type FreeformPreviewSlideId,
} from './freeformPreviewFixtures';
import useWindowSize from '@/hooks/useWindowSize';

const SLIDES: FreeformPreviewSlideId[] = [
  'theme',
  'color',
  'image',
  'markdown',
  'qr-off',
];

const PREVIEW_TITLE = 'Glow Sponsors';
const PREVIEW_CAPTION = 'Thank you for all the support!';
const PREVIEW_TITLE_LONG =
  'Glow Sponsors With An Extra Long Name That Should Ellipsis In The Pill';
const PREVIEW_CAPTION_LONG =
  'Thank you for all the support from everyone who made this evening possible and for staying with us through every slide.';

function PreviewJoinCta() {
  const { width, height } = useWindowSize();
  const [params] = useSearchParams();
  const frame = fitSlideFrame(width, height);
  const title =
    params.get('title') === 'long' ? PREVIEW_TITLE_LONG : PREVIEW_TITLE;
  const caption =
    params.get('caption') === 'long' ? PREVIEW_CAPTION_LONG : PREVIEW_CAPTION;
  const qrCodeProps = qrCodePropsFromFootprint(
    getFreeformStageQrFootprintPx(frame.width, frame.height),
  );
  const pillStyle = {
    ...slidePercentBoxStyle(slideQrPillMaxBox()),
    justifyContent: 'flex-end' as const,
    overflow: 'visible' as const,
  };
  const cardStyle = {
    ...slidePercentBoxStyle(SLIDE_QR_ZONE.card),
    alignItems: 'stretch' as const,
  };
  return (
    <div
      data-name="STAGE_CHROME_SLIDE_QR"
      style={slideFrameJoinOverlayStyle(frame)}
    >
      <div data-name="STAGE_JOIN_PILL" style={pillStyle}>
        <div
          className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-full bg-white px-2 text-center font-screen-body font-semibold text-[#593312]"
          style={{ fontSize: SLIDE_QR_LABEL_FONT_SIZE }}
        >
          {title}
        </div>
      </div>
      <div data-name="STAGE_JOIN_QR_CARD" style={cardStyle}>
        <div
          data-name="STAGE_QR"
          className="flex h-full w-full flex-col items-center rounded-lg bg-[#0e3a48]"
          style={{
            padding: `${frame.height * 0.02}px ${frame.width * 0.015}px`,
            boxSizing: 'border-box',
          }}
        >
          <div
            data-name="STAGE_QR_TEXT_SLOT"
            className="flex w-full shrink-0 items-center justify-center overflow-hidden"
            style={{ height: SLIDE_QR_CAPTION_RESERVE_FONT_SIZE }}
          >
            <p
              data-name="STAGE_QR_TEXT"
              className={`${stageQrCaptionClassName(true)} text-white`}
              style={{ fontSize: SLIDE_QR_LABEL_FONT_SIZE, width: '100%' }}
            >
              {caption}
            </p>
          </div>
          <div
            data-name="STAGE_QR_REMAINING"
            className="flex min-h-0 w-full flex-1 items-center justify-center"
          >
            <div
              data-name="STAGE_QR_CODE_TILE"
              className="overflow-hidden rounded-sm"
              style={{
                width: qrCodeProps.footprintPx,
                height: qrCodeProps.footprintPx,
                boxSizing: 'border-box',
                padding: qrCodeProps.quietZonePx,
                backgroundColor: STAGE_JOIN_QR_BG_COLOR,
              }}
            >
              <QRCode
                value="https://happiscreen.com/e/preview"
                size={qrCodeProps.size}
                bgColor={STAGE_JOIN_QR_BG_COLOR}
                fgColor={STAGE_JOIN_QR_FG_COLOR}
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  transform: 'rotate(180deg)',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SlidesFreeformPreviewPage() {
  const [params] = useSearchParams();
  const surface = params.get('surface') === 'fan' ? 'fan' : 'stage';
  const slideParam = params.get('slide') || 'theme';
  const slide = SLIDES.includes(slideParam as FreeformPreviewSlideId)
    ? (slideParam as FreeformPreviewSlideId)
    : 'theme';
  const staticShot = params.get('static') === '1';
  const slideData = useMemo(() => previewSlideData(slide), [slide]);
  const freeform = isFreeformLayout(slideData.layout);

  const stageCanvasStyles = stageStyles as unknown as StageSlideCanvasStyles;
  const entrance = staticShot ? undefined : stageCanvasStyles.enterFlat;

  if (surface === 'fan') {
    return (
      <div
        data-name="FAN_ROOT"
        data-preview-slide={slide}
        className="relative flex min-h-screen w-screen flex-col items-center pt-[10vh]"
        style={{
          background: STAGE_THEME_BACKGROUND,
          ['--screen-heading-font-family' as string]: 'Inter, sans-serif',
          ['--screen-body-font-family' as string]: 'Inter, sans-serif',
        }}
      >
        {freeform ? (
          <>
            <FanFreeformSlideCard
              slideId={slide}
              slideData={slideData}
              entranceClassName={staticShot ? undefined : fanStyles.enterFlat}
              themeFonts={{
                headingFontFamily: ['Inter'],
                bodyFontFamily: ['Inter'],
              }}
              themeBackdrop={{
                background: STAGE_THEME_BACKGROUND,
              }}
            />
            <FanReactionButtons
              reactionTypes={['👍', '🤩', '🎉', '👏']}
              onReaction={() => {}}
            />
          </>
        ) : (
          <div className="flex w-full flex-col items-center">
            {slideData.optionalImageUrl ? (
              <img
                className="relative z-10 -mb-36 h-[25vh] rounded bg-white p-2 shadow-sm"
                src={slideData.optionalImageUrl}
                alt="Slide Image"
              />
            ) : null}
            <div className={fanStyles.contentCard}>{slideData.content}</div>
            <FanReactionButtons
              reactionTypes={['👍', '🤩', '🎉', '👏']}
              onReaction={() => {}}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-name="STAGE_ROOT"
      data-preview-slide={slide}
      className="relative h-screen w-screen overflow-hidden"
      style={{
        background: STAGE_THEME_BACKGROUND,
        ['--screen-heading-font-family' as string]: 'Inter, sans-serif',
        ['--screen-body-font-family' as string]: 'Inter, sans-serif',
      }}
    >
      {freeform ? (
        <>
          <StageSlideCanvas
            currentSlideId="preview-slide"
            slideData={slideData}
            styles={{
              ...stageCanvasStyles,
              enterFlat: entrance || '',
            }}
          />
          {shouldShowFreeformStageJoinCta({
            layout: slideData.layout,
            showQr: slideData.showQr,
          }) ? (
            <PreviewJoinCta />
          ) : null}
        </>
      ) : (
        <div className="flex h-full min-h-0">
          <div className="relative z-10 mx-[2vw] my-[2vh] h-[calc(100%-4vh)] min-h-0 flex-1 px-[4vw] pb-[3vh] pt-[7vh]">
            <StageSlideCanvas
              currentSlideId="preview-slide"
              slideData={slideData}
              styles={{
                ...stageCanvasStyles,
                enterFlat: entrance || '',
              }}
            />
          </div>
          <div className="relative z-20 flex h-full w-[21vw] flex-col justify-end p-[2vh]">
            <div className="rounded bg-black/40 p-3 text-center text-white">
              Join
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
